import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    HStack,
    VStack,
    Text,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Badge,
    Divider,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody,
    FormControl,
    FormLabel,
    useDisclosure,
} from "@chakra-ui/react";

import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import { GoHomeFill } from "react-icons/go";

import HeaderBar from "../../components/Header/HeaderBar";
import LeftSidebar from "../../components/LeftSideBarLayout/LeftSideBar";
import Footer from "../../components/footer";
import axios from "axios";
import { Config } from "../../components/Utils/Config";
import { useNavigate } from "react-router-dom";
import { LuListFilter } from "react-icons/lu";


/* =========================================================
   STATIC SUBSCRIBER DATA
   ========================================================= */


/* =========================================================
   STAT CARD
   ========================================================= */

const StatCard = ({ label, value, helpText, accentColor, subValue }) => {
    return (
        <Box
            bg="#F0F4F9"
            borderRadius="12px"
            p="8px 18px"
            flex="1"
            minW="180px"
            position="relative"
            overflow="hidden"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "4px",
                height: "100%",
                bg: accentColor,
                borderRadius: "4px 0 0 4px",
            }}
        >
            <Text
                fontSize="12px"
                color="#8B8D97"
                mb="2px"
            >
                {label}
            </Text>

            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                <Text
                fontSize="22px"
                fontWeight="500"
                color="#45464E"
            >
                {value}
            </Text>

            <Text
                fontSize="16px"
                fontWeight="500"
                color="#45464E"
            >
                {subValue ? `Users: ${subValue}` : ""}
            </Text>
            </Box>

            {helpText && (
                <Text
                    fontSize="12px"
                    color="#8B8D97"
                    mt="2px"
                >
                    {helpText}
                </Text>
            )}
        </Box>
    );
};


const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getMonthsSubscribed = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return (
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth())
    );
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const Subscribers = () => {

    const invoiceTableColumns = [
        { text: "Name" },
        { text: "Invoice No" },
        { text: "Users" },
        { text: "Start Date", sortKey: "billing_period_start" },
        { text: "End Date", sortKey: "billing_period_end" },
        { text: "Purchase Date", sortKey: "purchase_date" },
        { text: "User Type" },
        { text: "Subtotal" },
        { text: "Discount Amt" },
        { text: "Coupon Code" },
        { text: "Taxable Amt" },
        { text: "GST%" },
        { text: "Gst Amt" },
        { text: "Total" },
        { text: "Status" },
        { text: "Months Subscribed" },
        { text: "Total Paid" },
    ];

    const sortInvoices = (key) => {
        const isAsc =
            invoiceSort.key === key ? !invoiceSort.asc : true;

        const sorted = [...subscribers].sort((a, b) => {
            const valA = a?.[key];
            const valB = b?.[key];

            // Handle empty dates
            if (!valA && !valB) return 0;
            if (!valA) return 1;
            if (!valB) return -1;

            // Compare dates
            const dateA = new Date(valA).getTime();
            const dateB = new Date(valB).getTime();

            if (dateA < dateB) return isAsc ? -1 : 1;
            if (dateA > dateB) return isAsc ? 1 : -1;

            return 0;
        });

        setInvoiceSort({ key, asc: isAsc });
        setSubscribers(sorted);
    };

    const [search, setSearch] = useState("");
    const navigate = useNavigate();
    const [userTypeFilter, setUserTypeFilter] = useState("");
    const [subscription, setSubscription] = useState("");
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [newType, setNewType] = useState("");


    const [totalSubscribers, setTotalSubscribers] = useState(0);
    const [totalFundsCollected, setTotalFundsCollected] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [hasMore, setHasMore] = useState(true);
    const [subscriptionStats, setSubscriptionStats] = useState({
        totalAmount: 0,
        totalTaxable: 0,
        totalPaidAmount: 0,
        totalDiscount: 0,
        totalOrganizations: 0,
        totalInvoices: 0,
        totalUsers: 0
    });

    const containerRef = useRef(null);
    const fetchingRef = useRef(false);

    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeTab, setActiveTab] = useState("invoices");
    const [newLoading, setNewLoading] = useState(false);
    const [newRegisters, setNewRegisters] = useState([]);
    const [totalNew, setTotalNew] = useState(0);
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [draftStartDate, setDraftStartDate] = useState("");
    const [draftEndDate, setDraftEndDate] = useState("");
    const [invoiceSort, setInvoiceSort] = useState({
        key: null,
        asc: true,
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    const handleCompanyClick = (subscriber) => {
        const userType = subscriber?.user?.userType;
        const userId = subscriber?.user?.id;

        if (!userId) return;

        if (userType === "Distributor") {
            navigate(`/distributors/business-details/${userId}`);
        } else if (userType === "Retailer") {
            navigate(`/retailers/business-details/${userId}`);
        } else if (userType === "Manufacturer") {
            navigate(`/manufacturers/business-details/${userId}`);
        }
    };

    const {
        isOpen: isDateFilterOpen,
        onOpen: onDateFilterOpen,
        onClose: onDateFilterClose,
    } = useDisclosure();

    const handleDateFilterOpen = () => {
        setDraftStartDate(newStartDate);
        setDraftEndDate(newEndDate);
        onDateFilterOpen();
    };

    const handleApplyDateFilter = () => {
        if (!draftStartDate || !draftEndDate) return;

        setNewStartDate(draftStartDate);
        setNewEndDate(draftEndDate);

        onDateFilterClose();
    };

    const handleClearDateFilter = () => {
        setDraftStartDate("");
        setDraftEndDate("");
        setNewStartDate("");
        setNewEndDate("");

        onDateFilterClose();
    };


    /* =====================================================
       STATS
       ===================================================== */

    //     const filteredByUserType = useMemo(() => {
    //     return DUMMY_SUBSCRIBERS.filter(
    //         (subscriber) =>
    //             userTypeFilter === "" ||
    //             subscriber.userType === userTypeFilter
    //     );
    // }, [userTypeFilter]);

    // const totalSubscribers = filteredByUserType.length;

    // const totalFundsCollected = filteredByUserType.reduce(
    //     (total, subscriber) => {
    //         const price = getPlanPrice(subscriber);

    //         return (
    //             total +
    //             price *
    //                 subscriber.usersForPlan *
    //                 subscriber.monthsSubscribed
    //         );
    //     },
    //     0
    // );


    /* =====================================================
       SEARCH + FILTER
       ===================================================== */

    // const filteredSubscribers = useMemo(() => {

    //     return DUMMY_SUBSCRIBERS.filter((subscriber) => {

    //         const searchValue = search.toLowerCase().trim();

    //         const matchesSearch =
    //             subscriber.name.toLowerCase().includes(searchValue) ||
    //             subscriber.planName.toLowerCase().includes(searchValue) ||
    //             subscriber.userType.toLowerCase().includes(searchValue) ||
    //             subscriber.id.toLowerCase().includes(searchValue);

    //         const matchesUserType =
    //             userTypeFilter === "" ||
    //             subscriber.userType === userTypeFilter;

    //         return matchesSearch && matchesUserType;
    //     });

    // }, [search, userTypeFilter]);

    const getSubStats = async () => {
        try {
            setStatsLoading(true);
            const res = await axios.get(`${Config.subs_stats}?userType=${userTypeFilter}&startDate=&endDate=`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                }
            });

            if (res?.data?.status === 200) {
                const data = res.data?.data;
                console.log(data, "DATA");
                setSubscriptionStats(res?.data?.data);
                setTotalFundsCollected(data?.totalPaidAmount ?? 0);
                setTotalSubscribers(data?.totalOrganizations ?? 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setStatsLoading(false);
        }
    };

    const getSubList = async (pageToFetch = 1, force = false) => {
        try {
            if (!force && fetchingRef.current) return;
            fetchingRef.current = true;
            setStatsLoading(true);

            const params = new URLSearchParams({
                userType: userTypeFilter,
                search: debouncedSearch,
                page: String(pageToFetch),
                limit: String(limit),
            });

            if (subscription) {
                params.append("expiryFilter", subscription);
            }
            const res = await axios.get(`${Config.subs_list}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                }
            });

            if (res?.data?.status === 200) {
                const data = res.data?.data;
                console.log(data, "DATA");
                const invoices = data?.invoices ?? [];
                const totalPage = data?.totalPage ?? data?.totalPages ?? 1;

                setSubscribers((prev) =>
                    pageToFetch === 1
                        ? invoices
                        : [...prev, ...invoices]
                );

                setCurrentPage(pageToFetch);

                if (pageToFetch >= totalPage) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }

            }
        } catch (error) {
            console.log(error);
        } finally {
            setStatsLoading(false);
            fetchingRef.current = false;
        }
    };

    useEffect(() => {
        getSubStats();
    }, [userTypeFilter]);

    const getNewRegList = async() => {
        try {
            setNewLoading(true);
            const res = await axios.get(`${Config.trial_users}?type=${newType}&startDate=${newStartDate}&endDate=${newEndDate}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                }
            });

            if (res?.data?.status === 200) {
                const data = res.data?.data;
                console.log(data, "DATA");
                setNewRegisters(data);
                setTotalNew(res.data?.total);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setNewLoading(false);
        }
    };


    useEffect(() => {
        setSubscribers([]);
        setCurrentPage(1);
        setHasMore(true);

        setInvoiceSort({
            key: null,
            asc: true,
        });

        getSubList(1, true);
    }, [userTypeFilter, debouncedSearch, subscription]);

    useEffect(() => {
        if (currentPage === 1) return;

        getSubList(currentPage);
    }, [currentPage]);

    const handleScroll = () => {
        const scrollArea = containerRef.current;

        if (!scrollArea) return;

        const {
            scrollHeight,
            clientHeight,
            scrollTop,
        } = scrollArea;

        const isNearBottom =
            scrollTop + clientHeight >= scrollHeight - 20;

        if (
            isNearBottom &&
            hasMore &&
            !fetchingRef.current
        ) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    useEffect(() => {
        if(activeTab === "new"){
            getNewRegList();
        } else {
            getSubList(1, true);
        }
    }, [activeTab, newType, newStartDate, newEndDate]);

    return (
        <Box
            backgroundColor="#F0F4F9"
            minH="100vh"
        >

            <HStack
                pl='20px' pr={"6px"} w={"100%"} spacing={0}
                alignItems="flex-start"
            >

                {/* =================================================
                    LEFT SIDEBAR
                   ================================================= */}

                <LeftSidebar />


                {/* =================================================
                    RIGHT CONTENT
                   ================================================= */}

                <Box as='main' minW={0} flex={1} ml={"6px"}>

                    <HeaderBar />


                    <Box
                        p={4}
                        bg="white"
                        mt="0rem"
                        padding="5px 12px"
                        borderRadius="15px 15px 0px 0px"
                    >

                        {/* =================================================
                            BREADCRUMB
                           ================================================= */}

                        <Breadcrumb
                            color="#8B8D97"
                            padding="0px 0px 0.5rem 0px"
                        >

                            <BreadcrumbItem>
                                <BreadcrumbLink href="/overview">
                                    <GoHomeFill color="#5570F1" />
                                </BreadcrumbLink>
                            </BreadcrumbItem>

                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    href="/subscribers"
                                    color="#8B8D97"
                                    fontSize="13px"
                                >
                                    Subscribers Management
                                </BreadcrumbLink>
                            </BreadcrumbItem>

                        </Breadcrumb>

                        <HStack width={"fit-content"} borderRadius={"5px"} px={"8px"} pb={"5px"} mb={"10px"} pt={"5px"} bg={"white"} boxShadow={"base"}>
                            <Button onClick={() => setActiveTab("invoices")} fontSize={"14px"} border={activeTab === "invoices" ? "2px solid gray": ""} color={activeTab === "invoices" ? "white" : "blackAlpha.800"} height={"36px"} bg={activeTab === "invoices" ? "purple.600" : "gray.100"} fontWeight={"500"} _hover={"none"}>Subscription Invoices</Button>
                            <Button border={activeTab === "new" ? "2px solid gray": ""} onClick={() => setActiveTab("new")} height={"36px"} fontSize={"14px"} color={activeTab === "new" ? "white" : "blackAlpha.800"}  bg={activeTab === "new" ? "purple.600" : "gray.100"} fontWeight={"500"} _hover={"none"} > New Registrations </Button>
                        </HStack>

                        {activeTab ==="invoices" ? <>
                        {/* =================================================
                            PAGE HEADER
                           ================================================= */}

                        <HStack
                            justifyContent="space-between"
                            mb="0.25rem"
                        >

                            <Box>
                                <Heading
                                    color="#45464E"
                                    fontSize="16px"
                                    fontWeight="600"
                                >
                                    Subscribers Management
                                </Heading>

                                <Text
                                    fontSize="11px"
                                    color="#8B8D97"
                                    mt="3px"
                                >
                                    Manage and monitor all subscription users
                                </Text>
                            </Box>

                        </HStack>


                        {/* =================================================
                            STAT CARDS
                           ================================================= */}

                        <HStack
                            gap="19px"
                            mb="0.5rem"
                        >
                            <StatCard
                                label="Total Subscribers/Organizations"
                                value={subscriptionStats.totalOrganizations}
                                subValue={subscriptionStats.totalUsers}
                                // helpText={
                                //     userTypeFilter
                                //         ? `${userTypeFilter} subscriptions`
                                //         : "All user type subscriptions"
                                // }
                                accentColor="#5570F1"
                            />
                            <StatCard
                                label="Total Invoice"
                                value={subscriptionStats.totalInvoices}
                                accentColor="#c53187"
                            />

                            <StatCard
                                label="Total Funds Collected"
                                value={`₹${totalFundsCollected.toLocaleString("en-IN")}`}
                                accentColor="#2A9D8F"
                            />
                            <StatCard
                                label="Total Discount"
                                value={`₹${subscriptionStats.totalDiscount.toLocaleString("en-IN")}`}
                                accentColor="#d17b2a"
                            />
                        </HStack>


                        {/* =================================================
                            TABLE CONTAINER
                           ================================================= */}

                        <Box
                            bg="white"
                            borderRadius="12px"
                            border="1px solid"
                            borderColor="gray.100"
                            overflow="hidden"
                        >

                            {/* =================================================
                                SEARCH + FILTER
                               ================================================= */}

                            <Box
                                px="16px"
                                pt="8px"
                                pb="0"
                            >

                                <HStack
                                    justify="space-between"
                                    mb="6px"
                                    spacing="10px"
                                >

                                    {/* SEARCH */}

                                    <InputGroup
                                        size="md"
                                        w="620px"
                                    >

                                        <InputLeftElement
                                            pointerEvents="none"
                                        >
                                            <SearchIcon
                                                color="#8B8D97"
                                                boxSize="12px"
                                            />
                                        </InputLeftElement>

                                        <Input
                                            placeholder="Search by name, plan or ID..."
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            _focus={{
                                                boxShadow: "none",
                                                bg: "#E8EEF7",
                                            }}
                                        />

                                    </InputGroup>

                                    <HStack>
                                        <Select
                                            size="sm"
                                            w="160px"
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={subscription}
                                            cursor={"pointer"}
                                            onChange={(e) =>
                                                setSubscription(e.target.value)
                                            }
                                            _focus={{
                                                boxShadow: "none",
                                            }}
                                        >

                                            <option value="">
                                                All
                                            </option>

                                            <option value="active">
                                                Active
                                            </option>

                                            <option value="expired">
                                                Expired
                                            </option>

                                        </Select>


                                        {/* USER TYPE FILTER */}

                                        <Select
                                            size="sm"
                                            w="160px"
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={userTypeFilter}
                                            onChange={(e) =>
                                                setUserTypeFilter(e.target.value)
                                            }
                                            cursor={"pointer"}
                                            _focus={{
                                                boxShadow: "none",
                                            }}
                                        >

                                            <option value="">
                                                All User Types
                                            </option>

                                            <option value="Retailer">
                                                Retailer
                                            </option>

                                            <option value="Distributor">
                                                Distributor
                                            </option>

                                        </Select>
                                    </HStack>

                                </HStack>


                                <Divider
                                    borderColor="gray.100"
                                />

                            </Box>


                            {/* =================================================
                                TABLE
                               ================================================= */}

                            <Box
                                ref={containerRef}
                                onScroll={handleScroll}
                                overflowX="auto"
                                overflowY="auto"
                                maxH="400px"
                            >

                                <Table
                                    variant="simple"
                                    size="sm"
                                >

                                    <Thead
                                        position="sticky"
                                        top={0}
                                        zIndex={2}
                                    >

                                        <Tr bg="#F0F4F9">

                                            {invoiceTableColumns.map((column) => (
                                                <Th
                                                    key={column.text}
                                                    fontSize="11px"
                                                    color="#8B8D97"
                                                    fontWeight="600"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.05em"
                                                    py="11px"
                                                    px="16px"
                                                    borderBottom="1px solid"
                                                    borderColor="gray.100"
                                                    whiteSpace="nowrap"
                                                >
                                                    <HStack spacing={2} align="center">
                                                        <Text>{column.text}</Text>

                                                        {column.sortKey && (
                                                            <Box
                                                                cursor="pointer"
                                                                display="flex"
                                                                alignItems="center"
                                                                color={
                                                                    invoiceSort.key === column.sortKey
                                                                        ? "purple.600"
                                                                        : "#8B8D97"
                                                                }
                                                                _hover={{ color: "purple.600" }}
                                                                onClick={() => sortInvoices(column.sortKey)}
                                                            >
                                                                <LuListFilter size={14} />
                                                            </Box>
                                                        )}
                                                    </HStack>
                                                </Th>

                                            ))}

                                        </Tr>

                                    </Thead>


                                    <Tbody>

                                        {subscribers.length === 0 ? (

                                            <Tr>

                                                <Td
                                                    colSpan={8}
                                                    textAlign="center"
                                                    py="40px"
                                                >

                                                    <Text
                                                        color="#8B8D97"
                                                        fontSize="14px"
                                                    >
                                                        No subscribers found.
                                                    </Text>

                                                </Td>

                                            </Tr>

                                        ) : (

                                            subscribers.map(
                                                (subscriber) => (

                                                    <Tr
                                                        key={subscriber.invoice_id}
                                                        _hover={{
                                                            bg: "#FAFBFC",
                                                        }}
                                                        borderBottom="1px solid"
                                                        borderColor="gray.50"
                                                    >

                                                        {/* NAME */}

                                                        <Td
                                                            px="16px"
                                                            py="6px"
                                                        >

                                                            <VStack
                                                                align="flex-start"
                                                                spacing="1px"
                                                            >

                                                                <Text
                                                                    fontSize="13px"
                                                                    fontWeight="600"
                                                                    color="#45464E"
                                                                    _hover={{ textDecoration: "underline", cursor: "pointer" }}
                                                                    onClick={() => handleCompanyClick(subscriber)}
                                                                >
                                                                    {subscriber?.user?.companyName}
                                                                </Text>

                                                                <Text
                                                                    fontSize="10px"
                                                                    color="#8B8D97"
                                                                >
                                                                    {subscriber?.user?.id}
                                                                </Text>

                                                            </VStack>

                                                        </Td>


                                                        {/* PLAN */}

                                                        {/* <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Badge
                                                                bg="#EEF3FF"
                                                                color="#3E60AA"
                                                                borderRadius="6px"
                                                                fontSize="11px"
                                                                px="9px"
                                                                py="3px"
                                                                fontWeight="600"
                                                            >
                                                                {subscriber.planName}
                                                            </Badge>

                                                        </Td> */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.invoice_no}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.items[0]?.quantity}
                                                            </Text>

                                                        </Td>


                                                        {/* START DATE */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {formatDate(subscriber?.billing_period_start)}
                                                            </Text>

                                                        </Td>


                                                        {/* END DATE */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {formatDate(subscriber?.billing_period_end)}
                                                            </Text>

                                                        </Td>


                                                        {/* LAST PAYMENT */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {formatDate(subscriber.purchase_date)}
                                                            </Text>

                                                        </Td>


                                                        {/* USER TYPE */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Badge
                                                                borderRadius="20px"
                                                                fontSize="11px"
                                                                fontWeight="600"
                                                                px="10px"
                                                                py="3px"
                                                                bg={
                                                                    subscriber?.user?.userType === "Retailer"
                                                                        ? "#E8F5E9"
                                                                        : subscriber.userType === "Distributor"
                                                                            ? "#F4F1FF"
                                                                            : "#FFF3E0"
                                                                }
                                                                color={
                                                                    subscriber?.user?.userType === "Retailer"
                                                                        ? "#2E7D32"
                                                                        : subscriber.userType === "Distributor"
                                                                            ? "#7B2FBE"
                                                                            : "#E65100"
                                                                }
                                                            >
                                                                {subscriber?.user?.userType}
                                                            </Badge>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.subtotal}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.discount_amount}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >
                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.coupon_code || "-"}
                                                            </Text>
                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.taxable_amount}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.gst}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.gstAmt}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.total}
                                                            </Text>

                                                        </Td>

                                                        {/* <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.amount_paid}
                                                            </Text>

                                                        </Td> */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Badge
                                                                borderRadius="20px"
                                                                fontSize="11px"
                                                                fontWeight="600"
                                                                px="10px"
                                                                py="3px"
                                                                textTransform="capitalize"
                                                                bg={
                                                                    subscriber?.status?.toLowerCase() === "paid"
                                                                        ? "#E8F5E9"
                                                                        : subscriber?.status?.toLowerCase() === "pending"
                                                                            ? "#FFF8E1"
                                                                            : subscriber?.status?.toLowerCase() === "failed"
                                                                                ? "#FFEBEE"
                                                                                : "#F0F4F9"
                                                                }
                                                                color={
                                                                    subscriber?.status?.toLowerCase() === "paid"
                                                                        ? "#2E7D32"
                                                                        : subscriber?.status?.toLowerCase() === "pending"
                                                                            ? "#F57F17"
                                                                            : subscriber?.status?.toLowerCase() === "failed"
                                                                                ? "#C62828"
                                                                                : "#6E7079"
                                                                }
                                                            >
                                                                {subscriber?.status || "-"}
                                                            </Badge>

                                                        </Td>


                                                        {/* USERS FOR PLAN */}

                                                        {/* <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                {subscriber.usersForPlan}
                                                            </Text>

                                                        </Td> */}


                                                        {/* MONTHS SUBSCRIBED */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                            display={"flex"}
                                                            justifyContent={"center"}
                                                            alignItems={"center"}
                                                        >

                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                {getMonthsSubscribed(subscriber?.billing_period_start, subscriber?.billing_period_end)}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >
                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                ₹
                                                                {subscriber?.amount_paid}
                                                            </Text>
                                                        </Td>

                                                    </Tr>

                                                )
                                            )

                                        )}

                                    </Tbody>

                                </Table>

                            </Box>


                            {/* =================================================
                                TABLE FOOTER
                               ================================================= */}

                            <HStack
                                justify="space-between"
                                px="20px"
                                py="12px"
                                borderTop="1px solid"
                                borderColor="gray.100"
                            >

                                <Text
                                    fontSize="12px"
                                    color="#8B8D97"
                                >

                                </Text>

                            </HStack>

                        </Box>
                        </> : <>
                        <Box
                            bg="white"
                            borderRadius="12px"
                            border="1px solid"
                            borderColor="gray.100"
                            overflow="hidden"
                        >

                            {/* =================================================
                                SEARCH + FILTER
                               ================================================= */}

                            <Box
                                px="16px"
                                pt="8px"
                                pb="0"
                            >

                                <HStack
                                    justifyContent={"space-between"}
                                    mb="6px"
                                    w={"full"}
                                    // spacing="10px"
                                >
                                    <Box>Total: {totalNew}</Box>


                                    {/* SEARCH */}

                                    {/* <InputGroup
                                        size="md"
                                        w="620px"
                                    >

                                        <InputLeftElement
                                            pointerEvents="none"
                                        >
                                            <SearchIcon
                                                color="#8B8D97"
                                                boxSize="12px"
                                            />
                                        </InputLeftElement>

                                        <Input
                                            placeholder="Search by name, plan or ID..."
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            _focus={{
                                                boxShadow: "none",
                                                bg: "#E8EEF7",
                                            }}
                                        />

                                    </InputGroup> */}

                                    <HStack w={"fit-content"}gap={1}>
                                        
                                    <Popover
                                        placement="bottom-end"
                                        isOpen={isDateFilterOpen}
                                        onOpen={handleDateFilterOpen}
                                        onClose={onDateFilterClose}
                                        closeOnBlur={false}
                                    >
                                        <PopoverTrigger>
                                            <Button
                                                size="sm"
                                                leftIcon={<CalendarIcon />}
                                                bg="#F0F4F9"
                                                color="#45464E"
                                                fontWeight="500"
                                                fontSize="13px"
                                                borderRadius="8px"
                                                _hover={{ bg: "#E8EEF7" }}
                                            >
                                                {newStartDate && newEndDate
                                                ? `${newStartDate.split("-").reverse().join("-")} - ${newEndDate.split("-").reverse().join("-")}`
                                                : "Date Filter"}
                                            </Button>
                                        </PopoverTrigger>

                                        <PopoverContent
                                            width="520px"
                                            borderRadius="10px"
                                            boxShadow="lg"
                                            _focus={{ outline: "none" }}
                                        >
                                            <PopoverArrow />
                                            <PopoverCloseButton />

                                            <PopoverHeader
                                                fontWeight="600"
                                                fontSize="14px"
                                                color="#45464E"
                                                py={3}
                                            >
                                                Filter by Registration Date
                                            </PopoverHeader>

                                            <PopoverBody p={4}>
                                                <HStack spacing={4} align="start">

                                                    {/* START DATE */}
                                                    <FormControl flex={1}>
                                                        <FormLabel
                                                            fontSize="12px"
                                                            fontWeight="600"
                                                            color="#45464E"
                                                        >
                                                            Start Date
                                                        </FormLabel>

                                                        <Input
                                                            type="date"
                                                            value={draftStartDate}
                                                            max={draftEndDate || undefined}
                                                            onChange={(e) =>
                                                                setDraftStartDate(e.target.value)
                                                            }
                                                            size="sm"
                                                            borderRadius="7px"
                                                            fontSize="13px"
                                                            height="38px"
                                                        />
                                                    </FormControl>

                                                    {/* END DATE */}
                                                    <FormControl flex={1}>
                                                        <FormLabel
                                                            fontSize="12px"
                                                            fontWeight="600"
                                                            color="#45464E"
                                                        >
                                                            End Date
                                                        </FormLabel>

                                                        <Input
                                                            type="date"
                                                            value={draftEndDate}
                                                            min={draftStartDate || undefined}
                                                            onChange={(e) =>
                                                                setDraftEndDate(e.target.value)
                                                            }
                                                            size="sm"
                                                            borderRadius="7px"
                                                            fontSize="13px"
                                                            height="38px"
                                                        />
                                                    </FormControl>

                                                </HStack>

                                                <Divider my={4} />

                                                <HStack justify="space-between">

                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        color="gray.600"
                                                        onClick={handleClearDateFilter}
                                                    >
                                                        Clear
                                                    </Button>

                                                    <HStack>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={onDateFilterClose}
                                                        >
                                                            Cancel
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            bg="purple.600"
                                                            color="white"
                                                            _hover={{ bg: "purple.700" }}
                                                            isDisabled={
                                                                !draftStartDate || !draftEndDate
                                                            }
                                                            onClick={handleApplyDateFilter}
                                                        >
                                                            Apply
                                                        </Button>
                                                    </HStack>

                                                </HStack>
                                            </PopoverBody>
                                        </PopoverContent>
                                    </Popover>

                                        {/* <Select
                                            size="sm"
                                            w="160px"
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={userTypeFilter}
                                            cursor={"pointer"}
                                            onChange={(e) =>
                                                setSubscription(e.target.value)
                                            }
                                            _focus={{
                                                boxShadow: "none",
                                            }}
                                        >

                                            <option value="">
                                                All
                                            </option>

                                            <option value="active">
                                                Active
                                            </option>

                                            <option value="expired">
                                                Expired
                                            </option>

                                        </Select> */}


                                        {/* USER TYPE FILTER */}

                                        <Select
                                            size="sm"
                                            w="160px"
                                            borderRadius="8px"
                                            fontSize="13px"
                                            bg="#F0F4F9"
                                            border="none"
                                            value={newType}
                                            onChange={(e) =>
                                                setNewType(e.target.value)
                                            }
                                            cursor={"pointer"}
                                            _focus={{
                                                boxShadow: "none",
                                            }}
                                        >

                                            <option value="">
                                                All User Types
                                            </option>

                                            <option value="Retailer">
                                                Retailer
                                            </option>

                                            <option value="Distributor">
                                                Distributor
                                            </option>

                                        </Select>
                                    </HStack>

                                </HStack>


                                <Divider
                                    borderColor="gray.100"
                                />

                            </Box>


                            {/* =================================================
                                TABLE
                               ================================================= */}

                            <Box
                                // ref={containerRef}
                                // onScroll={handleScroll}
                                overflowX="auto"
                                overflowY="auto"
                                maxH="400px"
                            >

                                <Table
                                    variant="simple"
                                    size="sm"
                                    tableLayout="fixed"
                                    width="100%"
                                    minWidth="1100px"
                                >

                                    <Thead
                                        position="sticky"
                                        top={0}
                                        zIndex={2}
                                    >

                                        <Tr bg="#F0F4F9">

                                            {[
                                                "Name",
                                                "User Type",
                                                "Status",
                                                "Email",
                                                "Phone",
                                                "Register Date",
                                                "Days Since Registered",
                                            ].map((column) => (

                                                <Th
                                                    key={column}
                                                    fontSize="11px"
                                                    color="#8B8D97"
                                                    fontWeight="600"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.05em"
                                                    py="11px"
                                                    px="16px"
                                                    borderBottom="1px solid"
                                                    borderColor="gray.100"
                                                    whiteSpace="nowrap"
                                                >
                                                    {column}
                                                </Th>

                                            ))}

                                        </Tr>

                                    </Thead>


                                    <Tbody>

                                        {subscribers.length === 0 ? (

                                            <Tr>

                                                <Td
                                                    colSpan={8}
                                                    textAlign="center"
                                                    py="40px"
                                                >

                                                    <Text
                                                        color="#8B8D97"
                                                        fontSize="14px"
                                                    >
                                                        No subscribers found.
                                                    </Text>

                                                </Td>

                                            </Tr>

                                        ) : (

                                            newRegisters.map(
                                                (subscriber) => (

                                                    <Tr
                                                        key={subscriber.invoice_id}
                                                        _hover={{
                                                            bg: "#FAFBFC",
                                                        }}
                                                        borderBottom="1px solid"
                                                        borderColor="gray.50"
                                                    >

                                                        {/* NAME */}

                                                        <Td
                                                            px="16px"
                                                            py="6px"
                                                            maxW={"200px"}
                                                        >

                                                            <VStack
                                                                align="flex-start"
                                                                spacing="1px"
                                                                maxW={"full"}
                                                            >

                                                                <Text
                                                                    fontSize="13px"
                                                                    fontWeight="600"
                                                                    color="#45464E"
                                                                    _hover={{ textDecoration: "underline", cursor: "pointer" }}
                                                                    onClick={() => handleCompanyClick(subscriber)}
                                                                >
                                                                    {subscriber?.userName}
                                                                </Text>

                                                                <Text
                                                                    fontSize="10px"
                                                                    color="#8B8D97"
                                                                >
                                                                    {subscriber?.userId}
                                                                </Text>

                                                            </VStack>

                                                        </Td>

                                                        {/* USER TYPE */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Badge
                                                                borderRadius="20px"
                                                                fontSize="11px"
                                                                fontWeight="600"
                                                                px="10px"
                                                                py="3px"
                                                                bg={
                                                                    subscriber?.userType === "Retailer"
                                                                        ? "#E8F5E9"
                                                                        : subscriber.userType === "Distributor"
                                                                            ? "#F4F1FF"
                                                                            : "#FFF3E0"
                                                                }
                                                                color={
                                                                    subscriber?.userType === "Retailer"
                                                                        ? "#2E7D32"
                                                                        : subscriber.userType === "Distributor"
                                                                            ? "#7B2FBE"
                                                                            : "#E65100"
                                                                }
                                                            >
                                                                {subscriber?.userType}
                                                            </Badge>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Badge
                                                                borderRadius="20px"
                                                                fontSize="11px"
                                                                fontWeight="600"
                                                                px="10px"
                                                                py="3px"
                                                                textTransform="capitalize"
                                                                bg={
                                                                    subscriber?.status?.toLowerCase() === "paid"
                                                                        ? "#E8F5E9"
                                                                        : subscriber?.status?.toLowerCase() === "pending"
                                                                            ? "#FFF8E1"
                                                                            : subscriber?.status?.toLowerCase() === "failed"
                                                                                ? "#FFEBEE"
                                                                                : "#F0F4F9"
                                                                }
                                                                color={
                                                                    subscriber?.status?.toLowerCase() === "paid"
                                                                        ? "#2E7D32"
                                                                        : subscriber?.status?.toLowerCase() === "pending"
                                                                            ? "#F57F17"
                                                                            : subscriber?.status?.toLowerCase() === "failed"
                                                                                ? "#C62828"
                                                                                : "#6E7079"
                                                                }
                                                            >
                                                                {subscriber?.status || "-"}
                                                            </Badge>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.email}
                                                            </Text>

                                                        </Td>

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {subscriber?.phone}
                                                            </Text>

                                                        </Td>


                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="12px"
                                                                color="#45464E"
                                                            >
                                                                {formatDate(subscriber?.onboardingDate)}
                                                            </Text>

                                                        </Td>


                                                        {/* USERS FOR PLAN */}

                                                        {/* <Td
                                                            px="16px"
                                                            py="13px"
                                                        >

                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                {subscriber.usersForPlan}
                                                            </Text>

                                                        </Td> */}


                                                        {/* MONTHS SUBSCRIBED */}

                                                        {/* <Td
                                                            px="16px"
                                                            py="13px"
                                                            display={"flex"}
                                                            justifyContent={"center"}
                                                            alignItems={"center"}
                                                        >

                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                {getMonthsSubscribed(subscriber?.billing_period_start, subscriber?.billing_period_end)}
                                                            </Text>

                                                        </Td> */}

                                                        <Td
                                                            px="16px"
                                                            py="13px"
                                                        >
                                                            <Text
                                                                fontSize="13px"
                                                                fontWeight="600"
                                                                color="#45464E"
                                                            >
                                                                
                                                                {subscriber?.daysSinceOnboarding}
                                                            </Text>
                                                        </Td>

                                                    </Tr>

                                                )
                                            )

                                        )}

                                    </Tbody>

                                </Table>

                            </Box>


                            {/* =================================================
                                TABLE FOOTER
                               ================================================= */}

                            <HStack
                                justify="space-between"
                                px="20px"
                                py="12px"
                                borderTop="1px solid"
                                borderColor="gray.100"
                            >

                                <Text
                                    fontSize="12px"
                                    color="#8B8D97"
                                >

                                </Text>

                            </HStack>

                        </Box>
                        </>}

                    </Box>


                    <Footer />

                </Box>

            </HStack>

        </Box>
    );
};

export default Subscribers;